class VariantPickerEnhanced extends HTMLElement {
  constructor() {
    super();
    
    this.abortController = null;
    this.currentVariant = null;
    
    const variantScript = this.querySelector('script[data-selected-variant-json]');
    if (variantScript) {
      try {
        this.currentVariant = JSON.parse(variantScript.textContent);
      } catch (e) {
        console.error('Error parsing variant data:', e);
      }
    }
  }

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.sectionId = this.dataset.section;
    this.sectionIdWithoutPrefix = this.dataset.sectionIdWithoutPrefix;
    this.productUrl = this.dataset.url;
    
    this.setupEventListeners();
    this.initializeVariantInput();
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  setupEventListeners() {
    this.addEventListener('change', this.handleVariantChange.bind(this));
  }

  initializeVariantInput() {
    if (this.currentVariant && this.currentVariant.id) {
      this.updateVariantInput(this.currentVariant.id);
      this.updateAddToCartButton(this.currentVariant.available);
    } else {
      this.updateAddToCartButton(false);
    }
  }

  async handleVariantChange(event) {
    const target = event.target;
    
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') {
      return;
    }

    const selectedOptionValueIds = this.getSelectedOptionValueIds();
    const newProductUrl = target.dataset.productUrl;
    const currentProductUrl = this.productUrl?.split('?')[0];
    const switchesProduct = newProductUrl && newProductUrl !== currentProductUrl;

    const requestUrl = this.buildRequestUrl(selectedOptionValueIds, switchesProduct ? newProductUrl : null);
    
    await this.fetchAndUpdateSection(requestUrl, switchesProduct);
    this.updateBrowserUrl(target, switchesProduct);
    this.dispatchVariantChangeEvent(target);
  }

  getSelectedOptionValueIds() {
    const checkedInputs = this.querySelectorAll('[data-variant-picker-option-inputs] input[type="radio"]:checked');
    return Array.from(checkedInputs)
      .map(input => input.dataset.optionValueId)
      .filter(Boolean);
  }

  buildRequestUrl(optionValueIds, productUrl = null) {
    const baseUrl = (productUrl || this.productUrl).split('?')[0];
    const params = [];

    if (optionValueIds.length > 0) {
      params.push(`option_values=${optionValueIds.join(',')}`);
    }

    params.push(`section_id=${this.sectionIdWithoutPrefix}`);

    return `${baseUrl}?${params.join('&')}`;
  }

  async fetchAndUpdateSection(url, switchesProduct = false) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    this.setFormLoading(true);
    this.setFormDisabled(true);

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newVariantScript = doc.querySelector('script[data-selected-variant-json]');
      if (newVariantScript) {
        try {
          this.currentVariant = JSON.parse(newVariantScript.textContent);
        } catch (e) {
        }
      }

      if (switchesProduct) {
        this.updateEntireSection(doc);
      } else {
        this.updateVariantPicker(doc);
        this.updateProductBlocks(doc);
      }

      if (this.currentVariant && this.currentVariant.id) {
        this.updateVariantInput(this.currentVariant.id);
        this.updateAddToCartButton(this.currentVariant.available);
        this.setFormDisabled(!this.currentVariant.available);
      } else {
        this.updateAddToCartButton(false);
        this.setFormDisabled(true);
      }

      this.setFormLoading(false);

    } catch (error) {
      this.setFormLoading(false);
      this.setFormDisabled(false);
    }
  }

  updateEntireSection(doc) {
    const currentSection = this.closest('.shopify-section');
    const newSection = doc.querySelector('.shopify-section');
    
    if (currentSection && newSection) {
      currentSection.innerHTML = newSection.innerHTML;
    }
  }

  updateVariantPicker(doc) {
    const newPicker = doc.querySelector('variant-picker');
    
    if (!newPicker) return;

    if (newPicker.dataset.productId) {
      this.dataset.productId = newPicker.dataset.productId;
    }
    if (newPicker.dataset.url) {
      this.dataset.url = newPicker.dataset.url;
      this.productUrl = newPicker.dataset.url;
    }

    const newList = newPicker.querySelector('.product-variant-picker__list');
    const currentList = this.querySelector('.product-variant-picker__list');
    
    if (newList && currentList) {
      currentList.innerHTML = newList.innerHTML;
    }

    const newForm = newPicker.querySelector('.product-variant-picker__form');
    const currentForm = this.querySelector('.product-variant-picker__form');
    
    if (newForm && currentForm) {
      const checkedValues = {};
      currentForm.querySelectorAll('input:checked').forEach(input => {
        checkedValues[input.name] = input.value;
      });
      
      currentForm.innerHTML = newForm.innerHTML;
      
      Object.entries(checkedValues).forEach(([name, value]) => {
        const input = currentForm.querySelector(`input[name="${name}"][value="${value}"]`);
        if (input) input.checked = true;
      });
    }

    const newScript = newPicker.querySelector('script[data-selected-variant-json]');
    const currentScript = this.querySelector('script[data-selected-variant-json]');
    
    if (newScript && currentScript) {
      currentScript.textContent = newScript.textContent;
    }
  }

  updateProductBlocks(doc) {
    const blockIds = [
      `product-price-${this.productId}-${this.sectionId}`,
      `product-stock-${this.productId}-${this.sectionId}`,
      `product-sku-${this.productId}-${this.sectionId}`,
      `product-badges-${this.productId}-${this.sectionId}`
    ];

    blockIds.forEach(blockId => {
      const currentBlock = document.getElementById(blockId);
      const newBlock = doc.getElementById(blockId);
      
      if (currentBlock && newBlock) {
        currentBlock.innerHTML = newBlock.innerHTML;
      }
    });
  }

  updateVariantInput(variantId) {
    const formId = `product-form-component-${this.productId}-${this.sectionId}`;
    const form = document.getElementById(formId);
    
    if (!form) {
      console.warn('Product form not found:', formId);
      return;
    }

    const variantInput = form.querySelector('input[name="id"]');
    
    if (variantInput) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      console.warn('Variant input not found in form');
    }
  }

  updateAddToCartButton(available) {
    const buttons = this.getAddToCartButtons();

    if (buttons.length === 0) {
      console.warn('No add to cart buttons found');
      return;
    }

    const addToCartText = window.auroraThemeLocales?.variantStrings?.addToCart || 'Dodaj do koszyka';
    const unavailableText = window.auroraThemeLocales?.variantStrings?.unavailable || 'Niedostępny';

    buttons.forEach(button => {
      const buttonTextSpan = button.querySelector('[data-button-text]');
      
      if (available) {
        button.disabled = false;
        button.setAttribute('aria-label', addToCartText);
        if (buttonTextSpan) {
          buttonTextSpan.textContent = addToCartText;
        }
      } else {
        button.disabled = true;
        button.setAttribute('aria-label', unavailableText);
        if (buttonTextSpan) {
          buttonTextSpan.textContent = unavailableText;
        }
      }
    });
  }

  setFormLoading(isLoading) {
    const buttons = this.getAddToCartButtons();
    buttons.forEach(button => {
      button.classList.toggle('loading', isLoading);
    });
  }

  setFormDisabled(isDisabled) {
    const buttons = this.getAddToCartButtons();
    buttons.forEach(button => {
      button.toggleAttribute('disabled', isDisabled);
    });
  }

  getAddToCartButtons() {
    return [
      document.getElementById(`product-buy-btn-${this.productId}-${this.sectionId}`),
      document.getElementById(`sticky-product-buy-btn-${this.productId}-${this.sectionId}`)
    ].filter(Boolean);
  }

  updateBrowserUrl(target, switchesProduct) {
    const isMainProductPage = this.dataset.updateUrl === 'true';
    
    if (!isMainProductPage) return;

    const url = new URL(window.location.href);

    if (target.dataset.variantId) {
      url.searchParams.set('variant', target.dataset.variantId);
    } else {
      url.searchParams.delete('variant');
    }

    if (switchesProduct && target.dataset.productUrl) {
      url.pathname = target.dataset.productUrl;
    }

    if (url.href !== window.location.href) {
      window.history.replaceState({}, '', url.toString());
    }
  }

  dispatchVariantChangeEvent(target) {
    this.dispatchEvent(new CustomEvent('variant:change', {
      detail: {
        variant: this.currentVariant,
        optionValueId: target.dataset.optionValueId,
        productId: this.productId,
        sectionId: this.sectionId
      },
      bubbles: true
    }));
  }

  getCurrentVariant() {
    return this.currentVariant;
  }
}

(function() {
  if (customElements.get('variant-picker')) {
    console.warn('variant-picker already registered');
    return;
  }
  
  customElements.define('variant-picker', VariantPickerEnhanced);
  
  const originalDefine = customElements.define.bind(customElements);
  customElements.define = function(name, constructor, options) {
    if (name === 'variant-picker') {
      return;
    }
    return originalDefine(name, constructor, options);
  };
  
  const originalGet = customElements.get.bind(customElements);
  customElements.get = function(name) {
    return originalGet(name);
  };
})();
